import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Divider,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Checkbox,
  ListItemText,
  Autocomplete,
  InputAdornment,
  Tooltip,
  Snackbar
} from '@mui/material';
import { 
  Save as SaveIcon, 
  ArrowBack as ArrowBackIcon, 
  Refresh as RefreshIcon, 
  Launch as LaunchIcon,
  LinkedIn as LinkedInIcon,
  Facebook as FacebookIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { talentService } from '../services/talentService';
import { opportunityService } from '../services/opportunityService';
import { useCurrentUser } from '../hooks/useCurrentUser';
import UserDisplay from '../components/UserDisplay';
import { DEAL_STAGES } from '../constants/dealStages';
import { v4 as uuidv4 } from 'uuid';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Invalid date';
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    } catch (err) {
        console.error('Error formatting date:', err);
        return 'Invalid date';
    }
};

const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    } catch (err) {
        console.error('Error formatting currency:', err);
        return 'N/A';
    }
};

const Talent = () => {
  const { talentId } = useParams();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [talent, setTalent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [talentCategories, setTalentCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);

  // Job History State
  const [jobHistoryPage, setJobHistoryPage] = useState(0);
  const [jobHistoryRowsPerPage, setJobHistoryRowsPerPage] = useState(10);
  const [jobHistorySortField, setJobHistorySortField] = useState('date'); // Default sort by date
  const [jobHistorySortDirection, setJobHistorySortDirection] = useState('desc'); // Default descending
  const [jobDialog, setJobDialog] = useState({ open: false, job: null, index: -1 });
  const [jobForm, setJobForm] = useState({
    opportunityId: '',
    date: '',
    rate: '',
    comment: ''
  });

  // Opportunity Selection State
  const [wonOpportunities, setWonOpportunities] = useState([]);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [loadingOpportunities, setLoadingOpportunities] = useState(false);

  // Job History Delete Confirmation State
  const [jobDeleteDialog, setJobDeleteDialog] = useState({ open: false, index: -1, job: null });
  const [deletingJob, setDeletingJob] = useState(false);
  const [savingJob, setSavingJob] = useState(false);

  // Rate Range State
  const [rateRanges, setRateRanges] = useState([]);
  const [newRateRange, setNewRateRange] = useState('');
  const [editingRateIndex, setEditingRateIndex] = useState(-1);
  const [editingRateValue, setEditingRateValue] = useState('');

  // Opportunity Search State
  const [opportunitySearchTerm, setOpportunitySearchTerm] = useState('');

  // Add this near the other state declarations
  const [loadingInitialOpportunities, setLoadingInitialOpportunities] = useState(false);

  const isEditing = talentId && talentId !== 'new';

  const loadTalent = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      talentService.validateConfiguration();
      const talentData = await talentService.getTalent(talentId);
      if (talentData) {
        setTalent(talentData);
        setSelectedCategories(talentService.formatCategoriesForDisplay(talentData.talentCategory));
        setRateRanges(parseRateRanges(talentData.rateRange));
      } else {
        setError('Talent not found');
      }
    } catch (err) {
      console.error('Error loading talent:', err);
      if (err.message.includes('Missing required environment variables')) {
        setError(`Configuration Error: ${err.message}. Please check your .env file.`);
      } else {
        setError(`Failed to load talent: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  }, [talentId]);

  const loadTalentCategories = useCallback(async () => {
    try {
      const categories = await talentService.getTalentCategories();
      setTalentCategories(categories);
    } catch (err) {
      console.error('Error loading talent categories:', err);
    }
  }, []);

  useEffect(() => {
    if (talentId === 'new') {
      // For new talent, initialize with empty values
      const emptyTalent = {
        ...talentService.createEmptyTalent(),
        jobsHistory: []
      };
      setTalent(emptyTalent);
      setRateRanges([]);
      setLoading(false);
    } else {
      loadTalent();
    }
    loadTalentCategories();
  }, [talentId, loadTalent, loadTalentCategories]);

  // Effect to set selected opportunity when editing a job and opportunities are loaded
  useEffect(() => {
    if (jobDialog.open && jobDialog.job && wonOpportunities.length > 0) {
      const opportunity = wonOpportunities.find(opp => opp.id === jobDialog.job.opportunityId);
      setSelectedOpportunity(opportunity || null);
    }
  }, [wonOpportunities, jobDialog]);

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setTalent(prev => ({ ...prev, [field]: value }));
    
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleCategoryChange = (event) => {
    const value = event.target.value;
    setSelectedCategories(typeof value === 'string' ? value.split(',') : value);
    setTalent(prev => ({ 
      ...prev, 
      talentCategory: talentService.formatCategoriesForStorage(typeof value === 'string' ? value.split(',') : value)
    }));
  };

  const validateForm = () => {
    const errors = {};
    
    if (!talent.firstName.trim()) {
      errors.firstName = 'First Name is required';
    }
    
    if (!talent.lastName.trim()) {
      errors.lastName = 'Last Name is required';
    }

    if (talent.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(talent.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // LinkedIn Profile validation
    if (talent.linkedInProfile && !talent.linkedInProfile.includes('linkedin.com/')) {
      errors.linkedInProfile = 'Please enter a valid LinkedIn URL (e.g., https://linkedin.com/in/username)';
    }

    // Facebook Page validation  
    if (talent.facebookPage && !talent.facebookPage.includes('facebook.com/')) {
      errors.facebookPage = 'Please enter a valid Facebook URL (e.g., https://facebook.com/username)';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      talentService.validateConfiguration();
      await talentService.saveTalent(talent, currentUser.email);
      setSuccess(isEditing ? 'Talent updated successfully' : 'Talent created successfully');
      
      // Navigate back to talents list after a short delay
      setTimeout(() => {
        navigate('/talents');
      }, 1500);
    } catch (err) {
      console.error('Error saving talent:', err);
      if (err.message.includes('Missing required environment variables')) {
        setError(`Configuration Error: ${err.message}. Please check your .env file.`);
      } else {
        setError(`Failed to save talent: ${err.message}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/talents');
  };

  const handleSocialMediaClick = (url, platform) => {
    if (url && url.trim()) {
      let fullUrl = url.trim();
      if (!fullUrl.startsWith('http://') && !fullUrl.startsWith('https://')) {
        fullUrl = `https://${fullUrl}`;
      }
      window.open(fullUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleGoogleMapsClick = () => {
    const addressParts = [];
    if (talent.street1) addressParts.push(talent.street1);
    if (talent.street2) addressParts.push(talent.street2);
    if (talent.city) addressParts.push(talent.city);
    if (talent.province) addressParts.push(talent.province);
    if (talent.country) addressParts.push(talent.country);
    if (talent.postalCode) addressParts.push(talent.postalCode);
    
    const fullAddress = addressParts.join(', ');
    if (fullAddress) {
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;
      window.open(googleMapsUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Job History Functions
  const handleJobHistoryAdd = () => {
    setJobForm({
        opportunityId: '',
        date: '',
        rate: '',
        comment: ''
    });
    setSelectedOpportunity(null);
    setJobDialog({ open: true, job: null, index: -1 });
  };

  const handleJobHistoryEdit = async (job, index) => {
    // Map the job data to the form fields
    setJobForm({
        opportunityId: job.opportunityId || '',
        date: job.jobDate ? new Date(job.jobDate).toISOString().split('T')[0] : '',
        rate: job.rate || '',
        comment: job.comment || ''
    });
    setJobDialog({ open: true, job, index });
    
    // We'll let the useEffect handle loading the opportunities
    // The selected opportunity will be set after the opportunities are loaded
  };

  const handleJobHistoryDelete = (index) => {
    if (!talent?.jobsHistory?.[index]) return;
    const job = talent.jobsHistory[index];
    setJobDeleteDialog({ open: true, index, job });
  };

  const handleJobDeleteConfirm = async () => {
    if (!talent?.jobsHistory?.[jobDeleteDialog.index]) return;
    
    setDeletingJob(true);
    try {
        const jobToDelete = talent.jobsHistory[jobDeleteDialog.index];
        await talentService.deleteJobHistory(jobToDelete.id);

        const updatedTalent = { ...talent };
        updatedTalent.jobsHistory = updatedTalent.jobsHistory.filter((_, i) => i !== jobDeleteDialog.index);
        setTalent(updatedTalent);
        setJobDeleteDialog({ open: false, index: -1, job: null });
    } catch (err) {
        console.error('Error deleting job history:', err);
        setError(`Failed to delete job history: ${err.message}`);
    } finally {
        setDeletingJob(false);
    }
  };

  const handleJobDeleteCancel = () => {
    setJobDeleteDialog({ open: false, index: -1, job: null });
  };

  const handleJobFormChange = (field) => (event) => {
    setJobForm(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleOpportunityChange = (event, newValue) => {
    setSelectedOpportunity(newValue);
    setJobForm(prev => ({
        ...prev,
        opportunityId: newValue?.id || ''
    }));
  };

  const handleOpportunitySearch = async (event, value, reason) => {
    // Skip search if the change was due to programmatic changes
    if (reason === 'reset' || reason === 'clear') {
        return;
    }

    if (!value) {
        // If search is cleared, show all won opportunities
        await loadWonOpportunities();
        return;
    }
    
    setLoadingOpportunities(true);
    try {
        const opportunities = await opportunityService.searchWonOpportunities(value);
        console.log('Search results:', opportunities); // Debug log
        setWonOpportunities(opportunities);
    } catch (err) {
        console.error('Error searching opportunities:', err);
        setError('Failed to search opportunities');
    } finally {
        setLoadingOpportunities(false);
    }
  };

  const handleJobSave = async () => {
    if (!jobForm.opportunityId || !jobForm.date) {
        return;
    }

    setSavingJob(true);
    try {
        // If talent is new (no ID), save it first
        if (!talent.id) {
            const savedTalent = await talentService.saveTalent(talent, currentUser.email);
            setTalent(savedTalent);
        }

        // Format the date to YYYY-MM-DD for SQL Server
        const formattedDate = new Date(jobForm.date).toISOString().split('T')[0];

        const newJob = {
            id: jobDialog.job?.id || uuidv4(), // Preserve existing ID or generate new one
            opportunityId: selectedOpportunity.id,
            opportunityName: selectedOpportunity.opportunityName,
            companyName: selectedOpportunity.companyName,
            contactName: selectedOpportunity.contactName,
            jobDate: formattedDate,
            rate: jobForm.rate,
            comment: jobForm.comment,
            createdDate: jobDialog.job?.createdDate || new Date().toISOString()
        };

        if (jobDialog.index === -1) {
            // Adding new job
            const savedJob = await talentService.createJobHistory(talent.id || talent.talentId, {
                id: newJob.id,
                opportunityId: newJob.opportunityId,
                jobDate: formattedDate,
                rate: newJob.rate,
                comment: newJob.comment
            });

            // Update local state with saved job
            const updatedTalent = { ...talent };
            if (!updatedTalent.jobsHistory) {
                updatedTalent.jobsHistory = [];
            }
            updatedTalent.jobsHistory.push({
                ...newJob,
                ...savedJob
            });
            setTalent(updatedTalent);
        } else {
            // Editing existing job
            const savedJob = await talentService.updateJobHistory(newJob.id, {
                opportunityId: newJob.opportunityId,
                jobDate: formattedDate,
                rate: newJob.rate,
                comment: newJob.comment
            });

            // Update local state with saved job
            const updatedTalent = { ...talent };
            updatedTalent.jobsHistory[jobDialog.index] = {
                ...newJob,
                ...savedJob
            };
            setTalent(updatedTalent);
        }

        setJobDialog({ open: false, job: null, index: -1 });
        setSelectedOpportunity(null);
        setJobForm({
            opportunityId: '',
            date: '',
            rate: '',
            comment: ''
        });
    } catch (err) {
        console.error('Error saving job history:', err);
        setError(`Failed to save job history: ${err.message}`);
    } finally {
        setSavingJob(false);
    }
  };

  const handleJobCancel = () => {
    setJobDialog({ open: false, job: null, index: -1 });
    setSelectedOpportunity(null);
  };

  const handleJobHistoryChangePage = (event, newPage) => {
    setJobHistoryPage(newPage);
  };

  const handleJobHistoryChangeRowsPerPage = (event) => {
    setJobHistoryRowsPerPage(parseInt(event.target.value, 10));
    setJobHistoryPage(0);
  };

  const handleJobHistorySort = (field) => {
    const isAsc = jobHistorySortField === field && jobHistorySortDirection === 'asc';
    setJobHistorySortDirection(isAsc ? 'desc' : 'asc');
    setJobHistorySortField(field);
  };

  const getOpportunityDisplayName = useCallback((job) => {
    if (!job?.opportunityId) return '-';
    let displayName = job.opportunityName || '';
    
    if (job.companyName) {
      displayName += ` - ${job.companyName}`;
    } else if (job.contactName) {
      displayName += ` - ${job.contactName}`;
    }
    
    return displayName || job.opportunityId;
  }, []);

  // Sort and paginate job history
  const sortedAndPaginatedJobHistory = React.useMemo(() => {
    if (!talent?.jobsHistory) return [];
    
    // Sort the job history
    const sorted = [...talent.jobsHistory].sort((a, b) => {
      let aValue, bValue;
      
      if (jobHistorySortField === 'date') {
        aValue = new Date(a.jobDate);
        bValue = new Date(b.jobDate);
      } else if (jobHistorySortField === 'opportunityName') {
        aValue = getOpportunityDisplayName(a).toLowerCase();
        bValue = getOpportunityDisplayName(b).toLowerCase();
      } else {
        aValue = a[jobHistorySortField] || '';
        bValue = b[jobHistorySortField] || '';
        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
      }
      
      if (jobHistorySortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
    
    // Paginate the sorted results
    return sorted.slice(
      jobHistoryPage * jobHistoryRowsPerPage,
      jobHistoryPage * jobHistoryRowsPerPage + jobHistoryRowsPerPage
    );
  }, [talent, jobHistoryPage, jobHistoryRowsPerPage, jobHistorySortField, jobHistorySortDirection, getOpportunityDisplayName]);

  // Helper functions for rate ranges
  const parseRateRanges = (rateRangeString) => {
    if (!rateRangeString) return [];
    return rateRangeString.split('|').filter(range => range.trim() !== '');
  };

  const formatRateRanges = (rateRangesArray) => {
    return rateRangesArray.filter(range => range.trim() !== '').join('|');
  };

  const handleAddRateRange = () => {
    if (newRateRange.trim() && !rateRanges.includes(newRateRange.trim())) {
      const updatedRanges = [...rateRanges, newRateRange.trim()];
      setRateRanges(updatedRanges);
      setTalent(prev => ({
        ...prev,
        rateRange: formatRateRanges(updatedRanges)
      }));
      setNewRateRange('');
    }
  };

  const handleRemoveRateRange = (indexToRemove) => {
    const updatedRanges = rateRanges.filter((_, index) => index !== indexToRemove);
    setRateRanges(updatedRanges);
    setTalent(prev => ({
      ...prev,
      rateRange: formatRateRanges(updatedRanges)
    }));
  };

  const handleNewRateRangeKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddRateRange();
    }
  };

  const handleEditRateRange = (index) => {
    setEditingRateIndex(index);
    setEditingRateValue(rateRanges[index]);
  };

  const handleSaveEditedRateRange = () => {
    if (editingRateValue.trim() && editingRateIndex >= 0) {
      const updatedRanges = [...rateRanges];
      
      // Check if the new value already exists (excluding the current item being edited)
      const isDuplicate = updatedRanges.some((range, index) => 
        index !== editingRateIndex && range === editingRateValue.trim()
      );
      
      if (!isDuplicate) {
        updatedRanges[editingRateIndex] = editingRateValue.trim();
        setRateRanges(updatedRanges);
        setTalent(prev => ({
          ...prev,
          rateRange: formatRateRanges(updatedRanges)
        }));
      }
    }
    setEditingRateIndex(-1);
    setEditingRateValue('');
  };

  const handleCancelEditRateRange = () => {
    setEditingRateIndex(-1);
    setEditingRateValue('');
  };

  const handleEditRateKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSaveEditedRateRange();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      handleCancelEditRateRange();
    }
  };

  // Add this function to load initial won opportunities
  const loadWonOpportunities = async () => {
    try {
        setLoadingInitialOpportunities(true);
        const opportunities = await opportunityService.searchWonOpportunities('');
        console.log('Received opportunities:', opportunities); // Debug log
        setWonOpportunities(opportunities);
    } catch (err) {
        console.error('Error loading won opportunities:', err);
        setError('Failed to load won opportunities');
    } finally {
        setLoadingInitialOpportunities(false);
    }
  };

  // Update the useEffect to handle setting the selected opportunity after loading
  useEffect(() => {
    if (jobDialog.open) {
        const loadInitialData = async () => {
            await loadWonOpportunities();
            // If we're editing, set the selected opportunity after loading the list
            if (jobDialog.job?.opportunityId) {
                const opportunity = wonOpportunities.find(opp => opp.id === jobDialog.job.opportunityId);
                if (opportunity) {
                    setSelectedOpportunity(opportunity);
                }
            }
        };
        loadInitialData();
    }
  }, [jobDialog.open]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!talent) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography variant="h6" color="text.secondary">
          Talent data not available
        </Typography>
      </Box>
    );
  }

  const paginatedJobHistory = sortedAndPaginatedJobHistory;

  return (
    <Box sx={{ width: '100%', pr: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            Back to Talents
          </Button>
          <Typography variant="h4">
            {isEditing ? 'Edit Talent' : 'Create Talent'}
          </Typography>
        </Box>
        
        {/* Refresh Button - Only show for existing talents */}
        {isEditing && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={() => loadTalent()}
              disabled={loading}
              title="Refresh data"
              sx={{ 
                color: 'primary.main',
                animation: loading ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': {
                    transform: 'rotate(0deg)',
                  },
                  '100%': {
                    transform: 'rotate(360deg)',
                  },
                }
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Box>
        )}
      </Box>

      <Paper elevation={3} sx={{ p: 3 }}>
        <Grid container spacing={isMobile ? 2 : 3}>
          {/* First Name */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="First Name"
              value={talent.firstName}
              onChange={handleInputChange('firstName')}
              error={!!validationErrors.firstName}
              helperText={validationErrors.firstName}
              required
            />
          </Grid>

          {/* Last Name */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Last Name"
              value={talent.lastName}
              onChange={handleInputChange('lastName')}
              error={!!validationErrors.lastName}
              helperText={validationErrors.lastName}
              required
            />
          </Grid>

          {/* Email */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={talent.email}
              onChange={handleInputChange('email')}
              error={!!validationErrors.email}
              helperText={validationErrors.email}
            />
          </Grid>

          {/* Phone */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Phone"
              value={talent.phone}
              onChange={handleInputChange('phone')}
            />
          </Grid>

          {/* LinkedIn Profile */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                fullWidth
                label="LinkedIn Profile"
                value={talent.linkedInProfile}
                onChange={handleInputChange('linkedInProfile')}
                error={!!validationErrors.linkedInProfile}
                helperText={validationErrors.linkedInProfile}
                placeholder="https://linkedin.com/in/username"
              />
              {talent.linkedInProfile && (
                <IconButton
                  onClick={() => handleSocialMediaClick(talent.linkedInProfile, 'LinkedIn')}
                  sx={{ 
                    color: '#0077b5',
                    '&:hover': { color: '#005885' }
                  }}
                  title="Open LinkedIn Profile"
                >
                  <LinkedInIcon />
                </IconButton>
              )}
            </Box>
          </Grid>

          {/* Facebook Page */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                fullWidth
                label="Facebook Page"
                value={talent.facebookPage}
                onChange={handleInputChange('facebookPage')}
                error={!!validationErrors.facebookPage}
                helperText={validationErrors.facebookPage}
                placeholder="https://facebook.com/username"
              />
              {talent.facebookPage && (
                <IconButton
                  onClick={() => handleSocialMediaClick(talent.facebookPage, 'Facebook')}
                  sx={{ 
                    color: '#1877f2',
                    '&:hover': { color: '#166fe5' }
                  }}
                  title="Open Facebook Page"
                >
                  <FacebookIcon />
                </IconButton>
              )}
            </Box>
          </Grid>

          {/* Talent Category */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Talent Categories</InputLabel>
              <Select
                multiple
                value={selectedCategories}
                onChange={handleCategoryChange}
                input={<OutlinedInput label="Talent Categories" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {talentCategories.map((category) => (
                  <MenuItem key={category} value={category}>
                    <Checkbox checked={selectedCategories.indexOf(category) > -1} />
                    <ListItemText primary={category} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Rate Range */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Rate Range
            </Typography>
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Add new rate range"
                value={newRateRange}
                onChange={(e) => setNewRateRange(e.target.value)}
                onKeyPress={handleNewRateRangeKeyPress}
                placeholder="e.g., $50-75/hour, $500-800/day"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleAddRateRange}
                        disabled={!newRateRange.trim() || rateRanges.includes(newRateRange.trim())}
                        size="small"
                      >
                        <AddIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {rateRanges.map((range, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                  {editingRateIndex === index ? (
                    <TextField
                      size="small"
                      value={editingRateValue}
                      onChange={(e) => setEditingRateValue(e.target.value)}
                      onKeyPress={handleEditRateKeyPress}
                      onBlur={handleSaveEditedRateRange}
                      autoFocus
                      sx={{ minWidth: '120px' }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={handleSaveEditedRateRange}
                              sx={{ p: 0.5 }}
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  ) : (
                    <Tooltip title="Click to edit" arrow>
                      <Chip
                        label={range}
                        onDelete={() => handleRemoveRateRange(index)}
                        onClick={() => handleEditRateRange(index)}
                        color="primary"
                        variant="outlined"
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'primary.50'
                          }
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
              ))}
              {rateRanges.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  No rate ranges added yet. Use the field above to add rate ranges.
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  💡 Click on any rate range to edit it, or use the × to delete it
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Status */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={talent.status}
                onChange={handleInputChange('status')}
                label="Status"
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Notes */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={talent.notes}
              onChange={handleInputChange('notes')}
            />
          </Grid>

          {/* Address Section */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 2 }}>
              <Divider sx={{ flexGrow: 1 }}>
                <Typography variant="h6" color="text.secondary">
                  Address Information
                </Typography>
              </Divider>
              <Button
                variant="outlined"
                size="small"
                startIcon={<LaunchIcon />}
                onClick={handleGoogleMapsClick}
                disabled={!talent.street1 && !talent.city && !talent.province && !talent.country}
                sx={{ ml: 2 }}
              >
                Link to Google Maps
              </Button>
            </Box>
          </Grid>

          {/* Street 1 */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Street 1"
              value={talent.street1}
              onChange={handleInputChange('street1')}
            />
          </Grid>

          {/* Street 2 */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Street 2"
              value={talent.street2}
              onChange={handleInputChange('street2')}
            />
          </Grid>

          {/* City */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="City"
              value={talent.city}
              onChange={handleInputChange('city')}
            />
          </Grid>

          {/* Province/State */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Province/State"
              value={talent.province}
              onChange={handleInputChange('province')}
            />
          </Grid>

          {/* Country */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Country"
              value={talent.country}
              onChange={handleInputChange('country')}
            />
          </Grid>

          {/* Postal Code */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Postal Code/Zip Code"
              value={talent.postalCode}
              onChange={handleInputChange('postalCode')}
            />
          </Grid>

          {/* Job History Section - Only show for existing talents */}
          {isEditing && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3, mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" component="h3">
                    Job History ({talent?.jobsHistory?.length || 0})
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleJobHistoryAdd}
                    size="small"
                  >
                    Add Job
                  </Button>
                </Box>

                {!talent?.jobsHistory?.length ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    No job history yet. Click "Add Job" to get started.
                  </Typography>
                ) : (
                  <>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: '30%' }}>
                              <TableSortLabel
                                active={jobHistorySortField === 'opportunityName'}
                                direction={jobHistorySortField === 'opportunityName' ? jobHistorySortDirection : 'asc'}
                                onClick={() => handleJobHistorySort('opportunityName')}
                              >
                                Opportunity
                              </TableSortLabel>
                            </TableCell>
                            {!isMobile && (
                              <TableCell sx={{ width: '15%', whiteSpace: 'nowrap' }}>
                                <TableSortLabel
                                  active={jobHistorySortField === 'date'}
                                  direction={jobHistorySortField === 'date' ? jobHistorySortDirection : 'asc'}
                                  onClick={() => handleJobHistorySort('date')}
                                >
                                  Date
                                </TableSortLabel>
                              </TableCell>
                            )}
                            {!isMobile && <TableCell sx={{ width: '15%' }}>Rate</TableCell>}
                            {!isMobile && <TableCell sx={{ width: '30%' }}>Comment</TableCell>}
                            <TableCell align="center" sx={{ width: '10%' }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paginatedJobHistory.map((job, index) => {
                            return (
                              <TableRow key={index} hover>
                                <TableCell sx={{ width: '30%' }}>
                                  {job.opportunityId ? (
                                    <Typography 
                                      variant="body2" 
                                      color="primary" 
                                      sx={{ 
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                        wordBreak: 'break-word'
                                      }}
                                      onClick={() => window.open(`/opportunities/${job.opportunityId}`, '_blank')}
                                    >
                                      {getOpportunityDisplayName(job)}
                                    </Typography>
                                  ) : (
                                    '-'
                                  )}
                                </TableCell>
                                {!isMobile && (
                                  <TableCell sx={{ width: '15%', whiteSpace: 'nowrap' }}>
                                    {job.jobDate ? new Date(job.jobDate).toLocaleDateString() : '-'}
                                  </TableCell>
                                )}
                                {!isMobile && (
                                  <TableCell sx={{ width: '15%' }}>{job.rate || '-'}</TableCell>
                                )}
                                {!isMobile && (
                                  <TableCell sx={{ width: '30%' }}>
                                    <Box sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                      {job.comment || '-'}
                                    </Box>
                                  </TableCell>
                                )}
                                <TableCell align="center" sx={{ width: '10%' }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleJobHistoryEdit(job, jobHistoryPage * jobHistoryRowsPerPage + index)}
                                    title="Edit Job"
                                  >
                                    <EditIcon />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleJobHistoryDelete(jobHistoryPage * jobHistoryRowsPerPage + index)}
                                    title="Delete Job"
                                    color="error"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    <TablePagination
                      component="div"
                      count={talent?.jobsHistory?.length || 0}
                      page={jobHistoryPage}
                      onPageChange={handleJobHistoryChangePage}
                      rowsPerPage={jobHistoryRowsPerPage}
                      onRowsPerPageChange={handleJobHistoryChangeRowsPerPage}
                      rowsPerPageOptions={[5, 10, 25]}
                    />
                  </>
                )}
              </Paper>
            </Grid>
          )}

          {/* Audit Information */}
          {isEditing && (
            <>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Created By
                  </Typography>
                  <UserDisplay email={talent.createdBy} size="medium" />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Created Date"
                  value={new Date(talent.createdDate).toLocaleString()}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </>
          )}
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Talent'}
          </Button>
        </Box>
      </Paper>

      {/* Job History Dialog */}
      <Dialog open={jobDialog.open} onClose={handleJobCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          {jobDialog.index === -1 ? 'Add Job History' : 'Edit Job History'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              fullWidth
              options={wonOpportunities}
              value={selectedOpportunity}
              onChange={handleOpportunityChange}
              onInputChange={handleOpportunitySearch}
              getOptionLabel={(option) => option?.label || ''}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              loading={loadingOpportunities || loadingInitialOpportunities}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Opportunity"
                  required
                  error={!jobForm.opportunityId && jobForm.date}
                  helperText={
                    !jobForm.opportunityId && jobForm.date 
                        ? "Please select an opportunity" 
                        : "Only showing opportunities won in the last 12 months"
                  }
                  placeholder="Type to search won opportunities..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {(loadingOpportunities || loadingInitialOpportunities) ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {option.opportunityName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatCurrency(option.closedValue)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <Typography variant="caption" color="text.secondary">
                        {option.companyName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(option.closedDate)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
              noOptionsText={loadingOpportunities || loadingInitialOpportunities ? "Loading..." : "No opportunities found"}
              filterOptions={(x) => x} // Disable client-side filtering
            />
            
            {/* Link to selected opportunity */}
            {selectedOpportunity && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography 
                  variant="body2" 
                  color="primary" 
                  sx={{ 
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                  onClick={() => window.open(`/opportunities/${selectedOpportunity.id}`, '_blank')}
                >
                  View Opportunity <OpenInNewIcon fontSize="small" />
                </Typography>
              </Box>
            )}
            
            <TextField
              fullWidth
              label="Date"
              type="date"
              value={jobForm.date}
              onChange={handleJobFormChange('date')}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="Rate"
              value={jobForm.rate}
              onChange={handleJobFormChange('rate')}
              placeholder="e.g., $75/hour, $600/day"
            />
            <TextField
              fullWidth
              label="Comment"
              multiline
              rows={3}
              value={jobForm.comment}
              onChange={handleJobFormChange('comment')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleJobCancel} disabled={savingJob}>Cancel</Button>
          <Button 
            onClick={handleJobSave} 
            variant="contained"
            disabled={!jobForm.opportunityId || !jobForm.date || savingJob}
            startIcon={savingJob ? <CircularProgress size={16} /> : null}
          >
            {savingJob ? 'Saving...' : (jobDialog.index === -1 ? 'Add' : 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Job History Delete Confirmation Dialog */}
      <Dialog
        open={jobDeleteDialog.open}
        onClose={handleJobDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm Job Deletion"}</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this job?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleJobDeleteCancel} disabled={deletingJob}>Cancel</Button>
          <Button 
            onClick={handleJobDeleteConfirm} 
            color="error"
            variant="contained"
            disabled={deletingJob}
            startIcon={deletingJob ? <CircularProgress size={16} /> : <DeleteIcon />}
            autoFocus
          >
            {deletingJob ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbars */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setError('')} 
          severity="error" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSuccess('')} 
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Talent;
